import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import UsCountyMap from './UsCountyMap';

interface CountyRate { state_code: string; county_fips: string; county_name: string; rate: string; active: boolean; }
interface StateOpt { state_code: string; state: string; county_based?: boolean; rate?: string | number }

// Manage per-county/parish destination tax rates for home-rule states (LA).
// Reuses the same county map as the dealers tab: click a parish → set its rate;
// unset parishes fall back to the state rate.
export default function ParishRatesPanel() {
  const [states, setStates] = useState<StateOpt[]>([]);
  const [state, setState] = useState('');
  const [rates, setRates] = useState<CountyRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ fips: string; name: string } | null>(null);
  const [rateInput, setRateInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.tax.listCollection().then((rows: any[]) => {
      const cb = (rows || []).filter(r => r.county_based);
      setStates(cb);
      setState(prev => prev || cb[0]?.state_code || '');
    }).catch(() => {});
  }, []);

  const loadRates = useCallback(async (sc: string) => {
    if (!sc) return;
    setLoading(true);
    try { setRates(await api.tax.getCountyRates(sc)); } catch { setRates([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (state) loadRates(state); }, [state, loadRates]);

  const ratedFips = rates.filter(r => Number(r.rate) > 0 && r.active).map(r => r.county_fips);
  const rateFor = (fips: string) => rates.find(r => r.county_fips === fips);

  const openParish = (fips: string, name: string) => {
    const cur = rateFor(fips);
    setModal({ fips, name });
    setRateInput(cur ? (Number(cur.rate) * 100).toString() : '');
    setMsg(null);
  };
  const save = async () => {
    if (!modal) return;
    const pct = parseFloat(rateInput);
    if (!(pct >= 0)) { setMsg('Enter a valid rate %'); return; }
    setSaving(true);
    try {
      await api.tax.setCountyRate(state, modal.fips, { rate: pct / 100, countyName: modal.name });
      setModal(null); await loadRates(state);
    } catch (e: any) { setMsg(e.message); } finally { setSaving(false); }
  };
  const clear = async () => {
    if (!modal) return;
    setSaving(true);
    try { await api.tax.clearCountyRate(state, modal.fips); setModal(null); await loadRates(state); }
    catch (e: any) { setMsg(e.message); } finally { setSaving(false); }
  };

  if (!states.length) return null;
  const fallback = states.find(s => s.state_code === state)?.rate;

  return (
    <div className="bg-white rounded-lg shadow p-5 mt-6">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Parish / County Rates</h3>
          <p className="text-sm text-gray-500">
            Click a parish to set its destination rate. Unset parishes fall back to the state rate
            {fallback ? ` (${(Number(fallback) * 100).toFixed(2)}%)` : ''}.
          </p>
        </div>
        <select value={state} onChange={e => setState(e.target.value)} className="border rounded px-3 py-1.5 text-sm">
          {states.map(s => <option key={s.state_code} value={s.state_code}>{s.state}</option>)}
        </select>
      </div>
      <p className="text-xs text-blue-700 mb-3">
        Look up official combined rates at{' '}
        <a href="https://remotesellersfiling.louisiana.gov/lookup/lookup.aspx" target="_blank" rel="noopener noreferrer" className="underline">
          LA Sales Tax Explorer
        </a>.
      </p>
      <div className="border rounded-lg overflow-hidden" style={{ background: '#1e293b' }}>
        <UsCountyMap
          visibleStates={[state]}
          owners={[{ id: 'set', name: 'Rate set', countyFips: ratedFips, color: '#16a34a' }]}
          selectedFips={modal?.fips || null}
          onCountyClick={(fips, name) => openParish(fips, name)}
        />
      </div>

      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : rates.length === 0 ? (
          <p className="text-sm text-gray-500">No parish rates set yet — click a parish on the map above.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Parish</th><th className="py-2 text-right">Rate</th><th className="py-2 text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.county_fips} className="border-b last:border-0">
                  <td className="py-2">{r.county_name || r.county_fips}</td>
                  <td className="py-2 text-right font-medium">{(Number(r.rate) * 100).toFixed(3)}%</td>
                  <td className="py-2 text-right">
                    <button onClick={() => openParish(r.county_fips, r.county_name)} className="text-blue-600 text-xs hover:underline">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h4 className="text-lg font-semibold text-gray-900 mb-1">{modal.name || modal.fips}</h4>
            <p className="text-xs text-gray-500 mb-4">FIPS {modal.fips} · {state}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Combined rate (%)</label>
            <input type="number" step="0.001" min="0" value={rateInput} onChange={e => setRateInput(e.target.value)}
              placeholder="e.g. 9.45" className="w-full border rounded px-3 py-2 text-sm" />
            {msg && <p className="text-sm text-red-600 mt-2">{msg}</p>}
            <div className="flex justify-between items-center mt-5">
              <button onClick={clear} disabled={saving} className="px-3 py-2 text-red-600 text-sm hover:bg-red-50 rounded">Clear (use fallback)</button>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="px-4 py-2 border rounded text-sm">Cancel</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
